/**
 * SubscriptionStatus Component
 * SESSION_186: Frontend Billing Page
 *
 * Displays subscription details including status, amount, billing dates,
 * and provides cancellation functionality.
 */

import React, { useState, useCallback } from 'react';
import {
  Paper,
  Stack,
  Group,
  Title,
  Text,
  Badge,
  Button,
  Divider,
  Alert,
  Modal,
  Grid
} from '@mantine/core';
import {
  IconCreditCard,
  IconCalendar,
  IconBuilding,
  IconAlertTriangle,
  IconCheck,
  IconClock
} from '@tabler/icons-react';
import { SubscriptionStatusProps } from './SubscriptionStatus.types';
import { SubscriptionStatus as StatusType } from '../../../application/dto/subscription';

/**
 * Get badge color based on subscription status.
 */
const getStatusBadgeColor = (status: StatusType): string => {
  switch (status) {
    case 'Active':
      return 'green';
    case 'PastDue':
      return 'orange';
    case 'Canceled':
      return 'red';
    case 'Pending':
      return 'blue';
    case 'Unpaid':
      return 'red';
    case 'Paused':
      return 'yellow';
    default:
      return 'gray';
  }
};

/**
 * Format date string to readable format.
 */
const formatDate = (dateString: string | null): string => {
  if (!dateString) {
    return 'N/A';
  }
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch {
    return 'N/A';
  }
};

/**
 * Format currency amount.
 * Note: Backend sends amount in dollars (not cents), so no division needed.
 */
const formatAmount = (amount: number, currency: string): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase()
  }).format(amount);
};

/**
 * SubscriptionStatus Component
 *
 * Displays subscription details and cancellation controls.
 */
export const SubscriptionStatus: React.FC<SubscriptionStatusProps> = ({
  subscription,
  onCancel,
  isCanceling = false
}): React.ReactElement => {
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const handleCancelClick = useCallback((): void => {
    setCancelModalOpen(true);
  }, []);

  const handleCancelConfirm = useCallback((): void => {
    setCancelModalOpen(false);
    onCancel?.();
  }, [onCancel]);

  const handleCancelModalClose = useCallback((): void => {
    setCancelModalOpen(false);
  }, []);

  const isActiveOrGrace = subscription.hasActiveAccess;
  const showGraceWarning = subscription.isInGracePeriod && subscription.daysRemainingInGrace !== null;

  return (
    <>
      <Paper withBorder p="md" radius="md">
        <Stack gap="md">
          {/* Header with Status Badge */}
          <Group justify="space-between" align="flex-start">
            <Group gap="md">
              <IconCreditCard size={24} />
              <Stack gap={4}>
                <Title order={4}>Subscription</Title>
                <Text size="sm" c="dimmed">
                  {subscription.locationCount} location{subscription.locationCount !== 1 ? 's' : ''}
                </Text>
              </Stack>
            </Group>
            <Badge
              color={getStatusBadgeColor(subscription.status)}
              variant="light"
              size="lg"
            >
              {subscription.statusDisplay}
            </Badge>
          </Group>

          {/* Grace Period Warning */}
          {showGraceWarning && (
            <Alert
              icon={<IconAlertTriangle size={16} />}
              title="Payment Past Due"
              color="orange"
              variant="light"
            >
              Your subscription payment failed. You have {subscription.daysRemainingInGrace} days
              remaining to update your payment method before your subscription is canceled.
            </Alert>
          )}

          {/* Pending Info - SESSION_204: Better messaging for pending status */}
          {subscription.status === 'Pending' && (
            <Alert
              icon={<IconClock size={16} />}
              title="Payment Processing"
              color="blue"
              variant="light"
            >
              Your subscription is being processed. This may take a few moments to complete.
              If this status persists, please contact support.
            </Alert>
          )}

          {/* Canceled Info */}
          {subscription.status === 'Canceled' && subscription.hasActiveAccess && (
            <Alert
              icon={<IconCheck size={16} />}
              title="Subscription Canceled"
              color="blue"
              variant="light"
            >
              Your subscription has been canceled but you still have access until the end of
              your billing period on {formatDate(subscription.currentPeriodEnd)}.
            </Alert>
          )}

          <Divider />

          {/* Subscription Details */}
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Stack gap={4}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Monthly Amount
                </Text>
                <Text size="lg" fw={600}>
                  {formatAmount(subscription.monthlyAmount, subscription.currency)}
                </Text>
              </Stack>
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Stack gap={4}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Locations
                </Text>
                <Text size="lg" fw={600}>
                  {subscription.locationCount}
                </Text>
              </Stack>
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Group gap="xs">
                <IconCalendar size={16} />
                <Stack gap={0}>
                  <Text size="xs" c="dimmed">
                    Current Period Started
                  </Text>
                  <Text size="sm">{formatDate(subscription.currentPeriodStart)}</Text>
                </Stack>
              </Group>
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Group gap="xs">
                <IconCalendar size={16} />
                <Stack gap={0}>
                  <Text size="xs" c="dimmed">
                    {subscription.status === 'Canceled' ? 'Access Until' : 'Next Billing Date'}
                  </Text>
                  <Text size="sm">{formatDate(subscription.currentPeriodEnd)}</Text>
                </Stack>
              </Group>
            </Grid.Col>
          </Grid>

          {/* Businesses List */}
          {subscription.businesses.length > 0 && (
            <>
              <Divider />
              <Stack gap="xs">
                <Text size="sm" fw={600}>
                  Included Businesses
                </Text>
                {subscription.businesses.map((business) => (
                  <Group key={business.businessId} gap="xs">
                    <IconBuilding size={14} />
                    <Text size="sm">{business.businessName}</Text>
                    {business.address && (
                      <Text size="xs" c="dimmed">
                        - {business.address}
                      </Text>
                    )}
                  </Group>
                ))}
              </Stack>
            </>
          )}

          {/* Cancel Button - SESSION_204: Also show for Pending status */}
          {(isActiveOrGrace || subscription.status === 'Pending') &&
            subscription.status !== 'Canceled' &&
            onCancel && (
            <>
              <Divider />
              <Button
                variant="subtle"
                color="red"
                onClick={handleCancelClick}
                loading={isCanceling}
                disabled={isCanceling}
              >
                Cancel Subscription
              </Button>
            </>
          )}
        </Stack>
      </Paper>

      {/* Cancellation Confirmation Modal - SESSION_204: Updated for Pending status */}
      <Modal
        opened={cancelModalOpen}
        onClose={handleCancelModalClose}
        title="Cancel Subscription?"
        centered
      >
        <Stack gap="md">
          {subscription.status === 'Pending' ? (
            <Text>
              Are you sure you want to cancel your subscription? Since your subscription is still
              being processed, cancellation will take effect immediately.
            </Text>
          ) : (
            <Text>
              Are you sure you want to cancel your subscription? You will continue to have access
              until the end of your current billing period on{' '}
              <strong>{formatDate(subscription.currentPeriodEnd)}</strong>.
            </Text>
          )}
          <Text size="sm" c="dimmed">
            After cancellation, your mention tracking will be paused and you will no longer
            receive notifications about new mentions.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={handleCancelModalClose}>
              Keep Subscription
            </Button>
            <Button
              color="red"
              onClick={handleCancelConfirm}
              loading={isCanceling}
            >
              Yes, Cancel
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
